import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { UserOrderService } from '../../services/order/UserOrderService';
import { UserOrderPanelDto } from '../../interfaces/UserOrderPanelDto';

const UserRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<UserOrderPanelDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [expandedOrders, setExpandedOrders] = useState<Record<number, boolean>>({});

  useEffect(() => {
    setLoading(true);
    setError('');

    UserOrderService.getUserRequests()
      .then((response) => {
        setOrders(response.data || []);
      })
      .catch((err: any) => {
        setOrders([]);
        setError(err?.data?.detail || 'Não foi possível carregar as solicitações.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, itemsPerPage]);

  const normalize = (value: string) =>
    (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const filteredOrders = orders.filter((order) => {
    if (!search) return true;
    const query = normalize(search);
    const description = normalize(order.description || '');
    const eventName = normalize(order.eventName || '');
    const approvedPlaces = (order.approvedPlaceOrders || [])
      .map((approved) => approved.placeName)
      .join(' ');
    const approvedNormalized = normalize(approvedPlaces);
    return (
      description.includes(query) ||
      eventName.includes(query) ||
      approvedNormalized.includes(query) ||
      String(order.orderCode).includes(query)
    );
  });

  const paginatedOrders = filteredOrders.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const pageCount = Math.ceil(filteredOrders.length / itemsPerPage);

  const formatDate = (value?: string | null) => {
    if (!value) return '';
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : value;
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return '';
    const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);
    const parsed = new Date(hasTimezone ? value : `${value}Z`);
    if (Number.isNaN(parsed.getTime())) return value;
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(parsed);
  };

  return (
    <Box
      sx={{
        maxWidth: 960,
        mx: 'auto',
        my: { xs: 8, md: 4 },
        px: { xs: 2, md: 3 },
      }}
    >
      <Stack spacing={3}>
        <Box>
          <Button
            startIcon={<ArrowBack />}
            variant="text"
            size="small"
            onClick={() => navigate('/')}
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              textTransform: 'none',
              px: 0,
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: 'transparent',
                color: 'text.primary',
              },
            }}
          >
            Voltar para início
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 1,
          }}
        >
          <Box
            component="img"
            src="https://temporario.umespaco.com/wp-content/uploads/2024/09/umespaco-com.png"
            alt="UmEspaco"
            sx={{ height: 48, width: 'auto' }}
          />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Minhas solicitações
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Acompanhe o status das solicitações enviadas.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            alignItems: { xs: 'stretch', sm: 'center' },
          }}
        >
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por descrição, espaço ou código"
            size="small"
            sx={{ minWidth: { xs: '100%', sm: 260 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl variant="standard" sx={{ minWidth: 100 }}>
            <InputLabel id="items-per-page-label">Itens</InputLabel>
            <Select
              labelId="items-per-page-label"
              value={itemsPerPage}
              onChange={(e) => {
                setPage(1);
                setItemsPerPage(Number(e.target.value));
              }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {!!error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && filteredOrders.length === 0 && (
          <Alert severity="info">Nenhuma solicitação encontrada.</Alert>
        )}

        {!loading && !error && filteredOrders.length > 0 && (
          <Stack spacing={2}>
            {paginatedOrders.map((order) => (
              <Paper
                key={order.orderId}
                elevation={0}
                sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, border: '1px solid #eee' }}
              >
                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between',
                      gap: 1,
                      alignItems: { xs: 'flex-start', sm: 'center' },
                    }}
                  >
                    <Box>
                      <Typography fontWeight={700}>Solicitação - {order.orderCode}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Data do evento: {formatDate(order.eventDate) || 'Não informada'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Nome do evento: {order.eventName || 'Não informado'}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                      Descrição
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {order.description || 'Sem descrição.'}
                    </Typography>
                  </Box>

                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        Espaços aprovados
                      </Typography>
                      <Link
                        component="button"
                        underline="hover"
                        onClick={() =>
                          setExpandedOrders((prev) => ({
                            ...prev,
                            [order.orderId]: !prev[order.orderId],
                          }))
                        }
                        sx={{ fontSize: 13, fontWeight: 600 }}
                      >
                        {expandedOrders[order.orderId] ? 'Ocultar' : 'Mostrar'}
                      </Link>
                    </Box>

                    {expandedOrders[order.orderId] && (
                      <Box>
                        {order.approvedPlaceOrders?.length ? (
                          <Stack spacing={1}>
                            {order.approvedPlaceOrders.map((approved) => (
                              <Box
                                key={approved.placeOrderId}
                                sx={{
                                  display: 'flex',
                                  flexDirection: { xs: 'column', sm: 'row' },
                                  justifyContent: 'space-between',
                                  gap: 1,
                                  bgcolor: '#f7f7f7',
                                  borderRadius: 1.5,
                                  px: 2,
                                  py: 1.5,
                                }}
                              >
                                <Typography fontWeight={600}>{approved.placeName}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Aprovado em {formatDateTime(approved.approveDate) || '---'}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Nenhum espaço aprovado até o momento.
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        {pageCount > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default UserRequestsPage;
